import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { INewsItem, INewsRequest } from "../../../interfaces/News.interface";
import type { ICompany } from "../../News/components/CompanyTag";
import { getNewsData, getNewsDetailData } from "../../../services/TradingMallService";
import { NewsDetailData } from "../../../pages/NewsDetail/types";
import type { NewsAEMContent } from "../../../interfaces/NewsAEM.interface";
import { PLACEHOLDER_IMAGE } from "../../../pages/News/newsTrackingConstants";

export interface NewsDetailSliderItem extends NewsDetailData {
  companies?: ICompany[];
}

interface UseNewsSliderParams {
  newsId: string;
  /**
   * Current news_type (trending, watchlist, portfolio, other)
   * Defaults to "other" when not provided.
   */
  newsType?: INewsRequest["newsType"] | "financials";
  aemContent?: NewsAEMContent | null;
  /**
   * Optional initial data from SSR. If provided, the hook will initialize
   * with this data instead of starting with empty items and loading state.
   */
  initialData?: NewsDetailSliderItem;
  /**
   * Optional initial related articles from SSR. If provided, the hook will use
   * these instead of fetching page 1 client-side, eliminating the initial API call.
   */
  initialRelatedArticles?: INewsItem[];
}

interface UseNewsSliderResult {
  items: NewsDetailSliderItem[];
  currentIndex: number;
  activeItem: NewsDetailSliderItem | null;
  isLoading: boolean;
  isFetchingMore: boolean;
  error: Error | null;
  hasPrevious: boolean;
  hasNext: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
}


const mapNewsItemToDetailData = (
  item: INewsItem,
  defaultSourceName: string = "ET",
  aemContent?: NewsAEMContent | null
): NewsDetailSliderItem => {
  // Use AEM body/description if available, otherwise fall back to API body
  const body = aemContent?.newsBody ?? aemContent?.newsDescription ?? item.body ?? "";

  return {
    id: item.id,
    image: item.image && item.image !== "" ? item.image : PLACEHOLDER_IMAGE,
    source: {
      name: item.source?.name ?? defaultSourceName,
      icon: item.source?.icon,
      avatar: item.source?.avatar ?? item.icon,
    },
    headline: item.title,
    timestamp: item.timestamp ?? item.time ?? "",
    stockTicker: {
      name: item.stockName ?? item.ticker ?? "",
      change: item.change ?? "",
      changeType: item.changeType ?? "positive",
    },
    body,
    companies: item.companies,
  };
};

/**
 * Checks if an item needs enrichment from the Detail API.
 * Since the Public News Filter API already returns full news objects (including body, image, companies),
 * we only need to enrich if the item is completely missing or has critical data gaps.
 * This function is now primarily used for the initial deep-linked article only.
 */
const needsItemEnrichment = (
  item: { body?: string; image?: string } | null | undefined,
): boolean => {
  // Only consider enrichment if item is missing or has no body AND no image
  // If Public API provides either body or image, we trust it and don't enrich
  return !item || (!item.body && (!item.image || item.image === PLACEHOLDER_IMAGE));
};

const addToIdSet = (prev: Set<string>, id: string) => {
  const next = new Set(prev);
  next.add(id);
  return next;
};

const removeFromIdSet = (prev: Set<string>, id: string) => {
  const next = new Set(prev);
  next.delete(id);
  return next;
};

const replaceItemById = <T extends { id: string }>(
  prev: T[],
  id: string,
  nextItem: T,
): T[] => {
  const index = prev.findIndex((item) => item.id === id);
  if (index < 0) return prev;
  const updated = [...prev];
  updated[index] = nextItem;
  return updated;
};

/**
 * @deprecated This function is no longer used for slide transitions.
 * The Public News Filter API already provides full news objects, so enrichment
 * is only needed for the initial deep-linked article (handled in ensureCurrentIdPresent).
 * Kept for potential future edge cases but should not be called during normal slider navigation.
 */
const enrichItemById = async ({
  itemId,
  items,
  enrichingItems,
  defaultSourceName,
  aemContent,
  setEnrichingItems,
  setItems,
}: {
  itemId: string;
  items: NewsDetailSliderItem[];
  enrichingItems: Set<string>;
  defaultSourceName: string;
  aemContent?: NewsAEMContent | null;
  setEnrichingItems: Dispatch<SetStateAction<Set<string>>>;
  setItems: Dispatch<SetStateAction<NewsDetailSliderItem[]>>;
}) => {
  const currentItem = items.find((item) => item.id === itemId);
  if (!needsItemEnrichment(currentItem) || enrichingItems.has(itemId)) return;

  setEnrichingItems((prev) => addToIdSet(prev, itemId));

  try {
    const detail = await getNewsDetailData(itemId);
    if (!detail) return;

    const enriched = mapNewsItemToDetailData(detail, defaultSourceName, aemContent);
    setItems((prev) => replaceItemById(prev, itemId, enriched));
  } catch {
    // Silently fail enrichment
  } finally {
    setEnrichingItems((prev) => removeFromIdSet(prev, itemId));
  }
};

const ensureListForPage = async <T>(
  pageToLoad: number,
  list: T,
  ensureForFirstPage: (value: T) => Promise<T>,
): Promise<T> => (pageToLoad === 1 ? ensureForFirstPage(list) : list);

const applyMappedItemsForPage = ({
  pageToLoad,
  mapped,
  newsId,
  setItems,
  setCurrentIndex,
  preserveInitialItem,
}: {
  pageToLoad: number;
  mapped: NewsDetailSliderItem[];
  newsId: string;
  setItems: Dispatch<SetStateAction<NewsDetailSliderItem[]>>;
  setCurrentIndex: (v: number) => void;
  preserveInitialItem?: NewsDetailSliderItem;
}) => {
  if (pageToLoad === 1) {
    // If we have an initial item to preserve (from SSR), merge it with fetched items
    if (preserveInitialItem) {
      // Check if the initial item is already in the mapped items
      const existingIndex = mapped.findIndex((item) => item.id === preserveInitialItem.id);
      if (existingIndex >= 0) {
        // Replace with the preserved item (which has full detail from SSR)
        const merged = [...mapped];
        merged[existingIndex] = preserveInitialItem;
        setItems(merged);
        setCurrentIndex(existingIndex);
      } else {
        // Prepend the initial item if it's not in the fetched list
        const merged = [preserveInitialItem, ...mapped];
        setItems(merged);
        setCurrentIndex(0);
      }
    } else {
      setItems(mapped);
      const initialIndex = mapped.findIndex((item) => item.id === newsId);
      setCurrentIndex(Math.max(initialIndex, 0));
    }
    return;
  }

  setItems((prev) => [...prev, ...mapped]);
};

type FetchableNewsType = "trending" | "other";

// React's `MutableRefObject` is deprecated in our React typings.
// For our internal helpers we only need the `{ current: T }` shape.
type RefCurrent<T> = { current: T };

type FetchContext = {
  isFetchingOther: boolean;
  effectivePage: number;
  effectivePageForCleanup: number;
  lastFetchedRef: RefCurrent<number>;
};

const getFetchableNewsType = (
  newsType: INewsRequest["newsType"] | "financials",
): FetchableNewsType => (newsType === "trending" ? "trending" : "other");

const getFetchContext = ({
  pageToLoad,
  hasExhaustedPrimary,
  otherNewsPageRef,
  lastFetchedPageRef,
  lastFetchedOtherPageRef,
}: {
  pageToLoad: number;
  hasExhaustedPrimary: boolean;
  otherNewsPageRef: RefCurrent<number>;
  lastFetchedPageRef: RefCurrent<number>;
  lastFetchedOtherPageRef: RefCurrent<number>;
}): FetchContext => {
  const isFetchingOther = hasExhaustedPrimary;
  const effectivePage = isFetchingOther ? otherNewsPageRef.current : pageToLoad;
  const lastFetchedRef = isFetchingOther ? lastFetchedOtherPageRef : lastFetchedPageRef;

  return {
    isFetchingOther,
    effectivePage,
    effectivePageForCleanup: effectivePage,
    lastFetchedRef,
  };
};

const shouldSkipFetch = ({
  isInitial,
  effectivePage,
  lastFetchedRef,
  fetchingPagesRef,
  isFetchInProgressRef,
  hasMoreRef,
  hasExhaustedPrimaryRef,
}: {
  isInitial: boolean;
  effectivePage: number;
  lastFetchedRef: RefCurrent<number>;
  fetchingPagesRef: RefCurrent<Set<number>>;
  isFetchInProgressRef: RefCurrent<boolean>;
  hasMoreRef: RefCurrent<boolean>;
  hasExhaustedPrimaryRef: RefCurrent<boolean>;
}) => {
  if (isInitial) return false;
  if (fetchingPagesRef.current.has(effectivePage)) return true;
  if (effectivePage <= lastFetchedRef.current) return true;
  if (isFetchInProgressRef.current) return true;
  if (!hasMoreRef.current && !hasExhaustedPrimaryRef.current) return true;
  return false;
};

const markFetchStart = ({
  isInitial,
  effectivePage,
  lastFetchedRef,
  fetchingPagesRef,
  isFetchInProgressRef,
}: {
  isInitial: boolean;
  effectivePage: number;
  lastFetchedRef: RefCurrent<number>;
  fetchingPagesRef: RefCurrent<Set<number>>;
  isFetchInProgressRef: RefCurrent<boolean>;
}) => {
  isFetchInProgressRef.current = true;
  if (isInitial) return;
  fetchingPagesRef.current.add(effectivePage);
  lastFetchedRef.current = Math.max(lastFetchedRef.current, effectivePage);
};

const resetInitialFetchState = ({
  setIsLoading,
  setError,
  setHasExhaustedPrimary,
  hasExhaustedPrimaryRef,
  setOtherNewsPage,
  otherNewsPageRef,
  fetchingPagesRef,
  lastFetchedPageRef,
  lastFetchedOtherPageRef,
  lastFetchTriggerIndexRef,
  pageRef,
  initialDetailFetchedRef,
}: {
  setIsLoading: (v: boolean) => void;
  setError: (v: Error | null) => void;
  setHasExhaustedPrimary: (v: boolean) => void;
  hasExhaustedPrimaryRef: RefCurrent<boolean>;
  setOtherNewsPage: Dispatch<SetStateAction<number>>;
  otherNewsPageRef: RefCurrent<number>;
  fetchingPagesRef: RefCurrent<Set<number>>;
  lastFetchedPageRef: RefCurrent<number>;
  lastFetchedOtherPageRef: RefCurrent<number>;
  lastFetchTriggerIndexRef: RefCurrent<number>;
  pageRef: RefCurrent<number>;
  initialDetailFetchedRef: RefCurrent<boolean>;
}) => {
  setIsLoading(true);
  setError(null);
  setHasExhaustedPrimary(false);
  hasExhaustedPrimaryRef.current = false;
  setOtherNewsPage(1);
  otherNewsPageRef.current = 1;
  fetchingPagesRef.current.clear();
  lastFetchedPageRef.current = 0;
  lastFetchedOtherPageRef.current = 0;
  lastFetchTriggerIndexRef.current = -1;
  pageRef.current = 1;
  initialDetailFetchedRef.current = false; // Reset to allow initial detail fetch for new newsId
};

const fetchTrendingThenMaybeFallback = async ({
  pageToLoad,
  limit,
  dedupeById,
  setHasExhaustedPrimary,
  hasExhaustedPrimaryRef,
  setOtherNewsPage,
  otherNewsPageRef,
  setHasMore,
}: {
  pageToLoad: number;
  limit: number;
  dedupeById: (list: INewsItem[]) => INewsItem[];
  setHasExhaustedPrimary: (v: boolean) => void;
  hasExhaustedPrimaryRef: RefCurrent<boolean>;
  setOtherNewsPage: Dispatch<SetStateAction<number>>;
  otherNewsPageRef: RefCurrent<number>;
  setHasMore: (v: boolean) => void;
}): Promise<INewsItem[]> => {
  const primary = await getNewsData({
    newsType: "trending",
    filters: [],
    page: pageToLoad,
    limit,
  });

  const combined = primary ?? [];
  const isExhausted =
    combined.length === 0 || (pageToLoad > 1 && combined.length < limit);

  if (!isExhausted) {
    setHasMore(combined.length >= limit);
    return combined;
  }

  setHasExhaustedPrimary(true);
  hasExhaustedPrimaryRef.current = true;

  let fallback: INewsItem[] = [];
  try {
    fallback = await getNewsData({
      newsType: "other",
      filters: [],
      page: 1,
      limit,
    }) ?? [];
  } catch {
    // Silently fail - continue with empty fallback
    fallback = [];
  }

  setOtherNewsPage(2);
  otherNewsPageRef.current = 2;
  setHasMore(!!fallback && fallback.length >= limit);

  return dedupeById([...combined, ...fallback]);
};

const fetchOtherNews = async ({
  pageToLoad,
  isInitial,
  limit,
  newsId,
  hasExhaustedPrimaryRef,
  otherNewsPageRef,
  setOtherNewsPage,
  setHasMore,
  dedupeById,
}: {
  pageToLoad: number;
  isInitial: boolean;
  limit: number;
  newsId: string;
  hasExhaustedPrimaryRef: RefCurrent<boolean>;
  otherNewsPageRef: RefCurrent<number>;
  setOtherNewsPage: Dispatch<SetStateAction<number>>;
  setHasMore: (v: boolean) => void;
  dedupeById: (list: INewsItem[]) => INewsItem[];
}): Promise<INewsItem[]> => {
  const isFetchingOther = hasExhaustedPrimaryRef.current;
  const effectivePage = isFetchingOther ? otherNewsPageRef.current : pageToLoad;

  const primary = await getNewsData({
    newsType: "other",
    filters: [],
    page: effectivePage,
    limit,
  });

  let combined = primary ?? [];

  if (isFetchingOther) {
    const hasMoreOther = combined.length >= limit;
    setHasMore(hasMoreOther);

    if (!isInitial && combined.length > 0 && hasMoreOther) {
      setOtherNewsPage((prev) => {
        const next = prev + 1;
        otherNewsPageRef.current = next;
        return next;
      });
    } else if (!isInitial && combined.length === 0) {
      setHasMore(false);
    }
  } else {
    setHasMore(combined.length >= limit);
  }

  // Keep existing behavior: on initial load, if list is empty or only contains current item,
  // fetch "other" again and merge (defensive fallback).
  if (
    isInitial &&
    (combined.length === 0 ||
      (combined.length === 1 && combined[0]?.id === newsId))
  ) {
    try {
      const fallback = await getNewsData({
        newsType: "other",
        filters: [],
        page: pageToLoad,
        limit,
      });
      combined = dedupeById([...combined, ...(fallback ?? [])]);
    } catch {
      // Silently fail - continue with existing combined data
    }
  }

  return combined;
};

const finalizeFetch = ({
  isInitial,
  effectivePageForCleanup,
  isFetchInProgressRef,
  setIsLoading,
  setIsFetchingMore,
  fetchingPagesRef,
}: {
  isInitial: boolean;
  effectivePageForCleanup: number;
  isFetchInProgressRef: RefCurrent<boolean>;
  setIsLoading: (v: boolean) => void;
  setIsFetchingMore: (v: boolean) => void;
  fetchingPagesRef: RefCurrent<Set<number>>;
}) => {
  isFetchInProgressRef.current = false;
  if (isInitial) {
    setIsLoading(false);
    fetchingPagesRef.current.clear();
    return;
  }
  setIsFetchingMore(false);
  fetchingPagesRef.current.delete(effectivePageForCleanup);
};

const fetchNewsData = async ({
  fetchableType,
  pageToLoad,
  isInitial,
  limit,
  newsId,
  hasExhaustedPrimaryRef,
  otherNewsPageRef,
  setOtherNewsPage,
  setHasExhaustedPrimary,
  setHasMore,
  dedupeById,
}: {
  fetchableType: FetchableNewsType;
  pageToLoad: number;
  isInitial: boolean;
  limit: number;
  newsId: string;
  hasExhaustedPrimaryRef: RefCurrent<boolean>;
  otherNewsPageRef: RefCurrent<number>;
  setOtherNewsPage: Dispatch<SetStateAction<number>>;
  setHasExhaustedPrimary: (v: boolean) => void;
  setHasMore: (v: boolean) => void;
  dedupeById: (list: INewsItem[]) => INewsItem[];
}): Promise<INewsItem[]> => {
  const shouldFetchTrending = fetchableType === "trending" && !hasExhaustedPrimaryRef.current;
  
  if (shouldFetchTrending) {
    return await fetchTrendingThenMaybeFallback({
      pageToLoad,
      limit,
      dedupeById,
      setHasExhaustedPrimary,
      hasExhaustedPrimaryRef,
      setOtherNewsPage,
      otherNewsPageRef,
      setHasMore,
    });
  }
  
  return await fetchOtherNews({
    pageToLoad,
    isInitial,
    limit,
    newsId,
    hasExhaustedPrimaryRef,
    otherNewsPageRef,
    setOtherNewsPage,
    setHasMore,
    dedupeById,
  });
};

const handleFetchError = ({
  err,
  initialData,
  isInitial,
  setError,
  setHasMore,
}: {
  err: unknown;
  initialData?: NewsDetailSliderItem;
  isInitial: boolean;
  setError: (v: Error | null) => void;
  setHasMore: (v: boolean) => void;
}): Error => {
  const e = err instanceof Error ? err : new Error("Failed to fetch news list");
  
  if (!initialData || isInitial) {
    setError(e);
  }
  
  if (!isInitial) {
    setHasMore(false);
  }
  
  return e;
};

export const useNewsSlider = ({
  newsId,
  newsType = "other",
  aemContent,
  initialData,
  initialRelatedArticles = [],
}: UseNewsSliderParams): UseNewsSliderResult => {
  const defaultSourceName = aemContent?.defaultSourceName ?? "ET";
  
  // Initialize items with initialData and related articles from SSR
  const initializeItems = (): { items: NewsDetailSliderItem[]; initialIndex: number } => {
    if (!initialData) return { items: [], initialIndex: 0 };
    
    // Map related articles to NewsDetailSliderItem format
    const relatedItems = initialRelatedArticles.map((item) =>
      mapNewsItemToDetailData(item, defaultSourceName, aemContent)
    );
    
    // Find if initialData is already in relatedItems
    const initialDataIndex = relatedItems.findIndex((item) => item.id === initialData.id);
    
    let items: NewsDetailSliderItem[];
    let index: number;
    
    if (initialDataIndex >= 0) {
      // Replace with initialData (which has full detail from SSR)
      items = [...relatedItems];
      items[initialDataIndex] = initialData;
      index = initialDataIndex;
    } else {
      // Prepend initialData if not found in related articles
      items = [initialData, ...relatedItems];
      index = 0;
    }
    
    return { items, initialIndex: index };
  };
  
  const { items: initialItems, initialIndex } = initializeItems();
  const [items, setItems] = useState<NewsDetailSliderItem[]>(initialItems);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [otherNewsPage, setOtherNewsPage] = useState(1); // Track page separately for "other" news
  const otherNewsPageRef = useRef(1); // Ref version to avoid unnecessary effect re-runs
  // If we have initialData and initialRelatedArticles, start with loading false since we already have data
  const [isLoading, setIsLoading] = useState(!(initialData && initialRelatedArticles.length > 0));
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const hasMoreRef = useRef(true);
  const [hasExhaustedPrimary, setHasExhaustedPrimary] = useState(false);
  const hasExhaustedPrimaryRef = useRef(false);
  const fetchingPagesRef = useRef<Set<number>>(new Set());
  const lastFetchedPageRef = useRef<number>(0);
  const lastFetchedOtherPageRef = useRef<number>(0); // Track last fetched "other" news page separately
  const isFetchInProgressRef = useRef<boolean>(false); // Track if any fetch is in progress
  const lastFetchTriggerIndexRef = useRef<number>(-1); // Track last index that triggered a fetch
  const pageRef = useRef<number>(1); // Track page in ref to avoid dependency
  const initialDetailFetchedRef = useRef<boolean>(false); // Track if initial detail API was called

  const [activeItem, setActiveItem] = useState(items.length > 0 ? items[initialIndex] : null);
  
  useEffect(() => {
  setActiveItem(items.length > 0 ? items[currentIndex] ?? null : null);
  },[currentIndex, items]);

  // Keep refs in sync with state
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    hasExhaustedPrimaryRef.current = hasExhaustedPrimary;
  }, [hasExhaustedPrimary]);

  useEffect(() => {
    otherNewsPageRef.current = otherNewsPage;
  }, [otherNewsPage]);

  const limit = 10;

  const dedupeById = (list: INewsItem[]): INewsItem[] => {
    const seen = new Set<string>();
    return list.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  /**
   * Ensures the initial deep-linked newsId is present and enriched with full detail data.
   * This is ONLY called on the initial page load (pageToLoad === 1) for the entry point article.
   * All subsequent slides use pre-fetched data from the Public News Filter API.
   * 
   * Strategy:
   * 1. If the item exists in the list with complete data (body and image), use it directly
   * 2. If the item is missing or has incomplete data, fetch from Detail API (one-time only)
   * 3. If the item is not in the list at all, fetch from Detail API and prepend it
   */
  const ensureCurrentIdPresent = useCallback(
    async (list: INewsItem[]): Promise<INewsItem[]> => {
      // Only fetch detail API once for the initial article
      if (initialDetailFetchedRef.current) {
        return list;
      }

      const currentIndex = list.findIndex((item) => item.id === newsId);
      const currentItem = currentIndex >= 0 ? list[currentIndex] : null;

      // Check if we truly need to fetch from Detail API
      // Trust Public API data - only fetch if item is missing or has no body AND no image
      const needsDetailFetch = needsItemEnrichment(currentItem);

      if (needsDetailFetch) {
        initialDetailFetchedRef.current = true;
        const resolvedNewsType = newsType as unknown as string | undefined;
        const detail =
          resolvedNewsType === "financials"
            ? await getNewsDetailData(newsId, "financials")
            : await getNewsDetailData(newsId);
        if (detail) {
          // Replace existing item or prepend if not found
          if (currentIndex >= 0) {
            const updated = [...list];
            updated[currentIndex] = detail;
            return updated;
          }
          return dedupeById([detail, ...list]);
        }
      } else {
        // Mark as fetched even if we didn't need to call the API
        // This prevents redundant calls on subsequent page loads
        initialDetailFetchedRef.current = true;
      }

      return list;
    },
    [newsId, newsType],
  );

  const fetchPage = useCallback(
    async (pageToLoad: number, isInitial: boolean) => {
      const ctx = getFetchContext({
        pageToLoad,
        hasExhaustedPrimary: hasExhaustedPrimaryRef.current,
        otherNewsPageRef,
        lastFetchedPageRef,
        lastFetchedOtherPageRef,
      });

      if (
        shouldSkipFetch({
          isInitial,
          effectivePage: ctx.effectivePage,
          lastFetchedRef: ctx.lastFetchedRef,
          fetchingPagesRef,
          isFetchInProgressRef,
          hasMoreRef,
          hasExhaustedPrimaryRef,
        })
      ) {
        return;
      }

      markFetchStart({
        isInitial,
        effectivePage: ctx.effectivePage,
        lastFetchedRef: ctx.lastFetchedRef,
        fetchingPagesRef,
        isFetchInProgressRef,
      });

      try {
        if (isInitial) {
          resetInitialFetchState({
            setIsLoading,
            setError,
            setHasExhaustedPrimary,
            hasExhaustedPrimaryRef,
            setOtherNewsPage,
            otherNewsPageRef,
            fetchingPagesRef,
            lastFetchedPageRef,
            lastFetchedOtherPageRef,
            lastFetchTriggerIndexRef,
            pageRef,
            initialDetailFetchedRef,
          });
        } else {
          setIsFetchingMore(true);
        }

        const fetchableType = getFetchableNewsType(newsType);
        const combined = await fetchNewsData({
          fetchableType,
          pageToLoad,
          isInitial,
          limit,
          newsId,
          hasExhaustedPrimaryRef,
          otherNewsPageRef,
          setOtherNewsPage,
          setHasExhaustedPrimary,
          setHasMore,
          dedupeById,
        });

        // Always ensure current item has full detail data (especially important for fallback scenarios)
        // This is critical when falling back to "other" news, as list items may lack detail fields
        const ensured = await ensureListForPage(
          pageToLoad,
          combined,
          ensureCurrentIdPresent,
        );

        const mapped = ensured.map((item) =>
          mapNewsItemToDetailData(item, defaultSourceName, aemContent),
        );

        applyMappedItemsForPage({
          pageToLoad,
          mapped,
          newsId,
          setItems,
          setCurrentIndex,
          preserveInitialItem: initialData && pageToLoad === 1 ? initialData : undefined,
        });

        // Update last fetched page to prevent re-fetching (already done at the start, but ensure it's set)
        // If we have no more data, set to Infinity to stop all future fetches
        if (!hasMore && !hasExhaustedPrimaryRef.current) {
          lastFetchedPageRef.current = Infinity;
          lastFetchedOtherPageRef.current = Infinity;
        }
        
        pageRef.current = pageToLoad;
      } catch (err) {
        const e = handleFetchError({
          err,
          initialData,
          isInitial,
          setError,
          setHasMore,
        });
        // Re-throw error so caller can handle it if needed (e.g., in useEffect catch)
        throw e;
      } finally {
        finalizeFetch({
          isInitial,
          effectivePageForCleanup: ctx.effectivePageForCleanup,
          isFetchInProgressRef,
          setIsLoading,
          setIsFetchingMore,
          fetchingPagesRef,
        });
      }
    },
    [limit, newsId, newsType, ensureCurrentIdPresent, hasMore, defaultSourceName, initialData],
  );

  // Initial fetch - only when newsId or newsType changes
  // Skip initial fetch if we have initialData AND initialRelatedArticles (from SSR)
  useEffect(() => {
    if (initialData) {
      initialDetailFetchedRef.current = true;
      
      // If we have pre-fetched related articles from SSR, mark page 1 as already fetched
      if (initialRelatedArticles.length > 0) {
        lastFetchedPageRef.current = 1;
        lastFetchedOtherPageRef.current = 1;
        setHasMore(initialRelatedArticles.length >= limit);
        // No need to fetch - we already have the data from SSR
        return;
      }
      
      // If we only have initialData but no related articles, fetch page 1
      // but don't treat it as initial loading since we already have data
      fetchPage(1, false).catch((error) => {
        // Silently handle fetch errors when we have initial data
        // The user can still see the current article, and we can retry later if needed
        console.warn("Failed to fetch related news items, continuing with initial data:", error);
      });
    } else {
      fetchPage(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsId, newsType]);

  // Fetch next page when approaching end - use ref to avoid dependency on fetchPage
  useEffect(() => {
    if (!items.length || isLoading) {
      return;
    }

    // Prevent if a fetch is already in progress
    if (isFetchInProgressRef.current) {
      return;
    }

    // If trending is exhausted, we should still allow fetching "other" news
    // So only check hasMore if we haven't exhausted primary
    if (!hasMore && !hasExhaustedPrimaryRef.current) {
      return;
    }

    const indexInCurrentPage = currentIndex % limit;
    const approachingEndOfPage = indexInCurrentPage >= 6;
    
    // Only trigger fetch if currentIndex has changed since last fetch trigger
    // This prevents fetching when page state updates but user hasn't scrolled
    // We allow fetching again when user scrolls to a different index
    if (currentIndex === lastFetchTriggerIndexRef.current && approachingEndOfPage) {
      return;
    }
    
    // Calculate next page based on whether trending is exhausted
    // When trending is exhausted, use otherNewsPageRef (which is already incremented after fetch)
    // When not exhausted, use pageRef + 1
    const isFetchingOther = hasExhaustedPrimaryRef.current;
    const nextPage = isFetchingOther ? otherNewsPageRef.current : pageRef.current + 1;
    const isAlreadyFetching = fetchingPagesRef.current.has(nextPage);
    const lastFetchedRef = isFetchingOther ? lastFetchedOtherPageRef : lastFetchedPageRef;
    const alreadyFetched = nextPage <= lastFetchedRef.current;

    // Only fetch if:
    // 1. Approaching end of current page
    // 2. Has more data available (or trending is exhausted, allowing fallback)
    // 3. Not currently fetching more
    // 4. Not already fetching this specific page
    // 5. Haven't already fetched this page
    // 6. We have items to show (prevent fetching when items array is empty)
    // 7. No fetch is in progress (double-check with ref)
    if (approachingEndOfPage && (hasMore || hasExhaustedPrimaryRef.current) && !isFetchingMore && !isAlreadyFetching && !alreadyFetched && items.length > 0 && !isFetchInProgressRef.current) {
      lastFetchTriggerIndexRef.current = currentIndex; // Mark this index as having triggered a fetch
      // Use the latest fetchPage function via closure
      fetchPage(nextPage, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasMore, isFetchingMore, items.length, limit, isLoading, hasExhaustedPrimary]);



  /**
   * REMOVED: Enrichment useEffect that was calling Detail API on every slide change.
   * 
   * Optimization: The Public News Filter API already returns full news objects
   * (including body, image, companies, etc.) in its response. Therefore, we:
   * 1. Only call Detail API for the initial deep-linked article (handled in ensureCurrentIdPresent)
   * 2. Use pre-fetched Public API data directly for all subsequent slides
   * 3. Eliminate redundant API calls and achieve zero-layout-shift transitions
   * 
   * This ensures instant slide transitions without waiting for API responses.
   */

  useEffect(() => {
    if (!items.length) return;

    const indicesToPrefetch = [currentIndex + 1, currentIndex + 2];

    indicesToPrefetch.forEach((index) => {
      const item = items[index];
      if (!item?.image) return;

      const img = new Image();
      img.src = item.image;
    });
  }, [currentIndex, items]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
  };

  return {
    items,
    currentIndex,
    activeItem: activeItem ?? null,
    isLoading,
    isFetchingMore,
    error,
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
  };
};


